import { Big } from 'big.js';
import {
  type AccountID,
  MsgTypeOrderRequest,
  type Order,
  OrderFlagFillOrKill,
  OrderFlagGoodTillCancel,
  OrderFlagImmediateOrCancel,
  OrderFlagPostOnly,
  type OrderFlags,
  type OrderRequest,
  OrderTypeCancel,
  OrderTypeChange,
  OrderTypeCloseLong,
  OrderTypeCloseShort,
  OrderTypeIncreasePositionCollateral,
  OrderTypeOpenLong,
  OrderTypeOpenShort,
  type RequestID,
} from '@/api/spec.ts';
import { Side } from '@/common/constants.ts';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { OrderType, type SLTPType, TiF } from '@/feature/order_entry/order_types.ts';
import { bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';
import { getMarketDataById } from './helpers.ts';
import { leverageForApi } from './leverageUtils.ts';
import type { OrderEditChanges } from './orderEditUtils.ts';
import { assertExchangeTokenAvailable, determineFinalPrice, extractPriceValues } from './orderFormValues.ts';
import { canImplicitClose } from './orderSizing.ts';
import { calculateTriggerPrice, getStopTriggerCondition, getTpSlTriggerCondition, shouldTpSlOrdersUseIoc } from './tpSlUtils';
import { resolveUserSlippagePercent } from './userSlippage.ts';

/**
 * Let the engine set the execution deadline. On `lb: 0` it uses `head_at_forward + ttl`, on a
 * non-zero `lb` it takes `min(lb, head_at_forward + ttl)` — same ceiling, so nothing is lost.
 *
 * Computing one costs correctness: it would come from the *client's* `headBlockNumber`, which only
 * advances on heartbeats, and the gateway rejects `0 < lb <= its own head` with `400 last exec
 * block already expired`. A heartbeat stalled for `order_ttl_blocks` (~8s) on an otherwise healthy
 * socket would reject every order until it recovers.
 */
const NO_LAST_EXEC_BLOCK = 0;

const typeMapOnApi = {
  [Side.long]: {
    open: OrderTypeOpenLong,
    close: OrderTypeCloseShort,
  },
  [Side.short]: {
    open: OrderTypeOpenShort,
    close: OrderTypeCloseLong,
  },
};

export function buildApiOrder(
  formData: OrderFormData,
  accountId: AccountID,
  requestId: RequestID,
  marketId: number
): OrderRequest {
  assertExchangeTokenAvailable();
  const marketData = getMarketDataById(marketId);
  if (marketData?.id == null) {
    throw new Error('Market not found for the given marketId');
  }

  const priceValues = extractPriceValues(formData, marketData);
  const isMarketOrder = formData.orderType === OrderType.market || formData.orderType === OrderType.stopMarket;
  const isTriggerOrder = formData.orderType === OrderType.stopLimit || formData.orderType === OrderType.stopMarket;
  // send zero price for market orders
  const finalPriceBigInt = isMarketOrder ? 0n : determineFinalPrice(formData, priceValues);
  const triggerCondition =
    'triggerCondition' in formData && formData.triggerCondition != null
      ? formData.triggerCondition
      : getStopTriggerCondition(formData.side);

  const tif = 'tif' in formData ? formData.tif : undefined;
  let flags: OrderFlags = OrderFlagGoodTillCancel;
  if (formData.orderType === OrderType.market || tif === TiF.immediateOrCancel) {
    flags |= OrderFlagImmediateOrCancel;
  } else if (tif === TiF.fillOrKill) {
    flags |= OrderFlagFillOrKill;
  } else if (tif === TiF.postOnly) {
    flags |= OrderFlagPostOnly;
  }

  const isImplicitClose = canImplicitClose(
    formData.side,
    priceValues.position,
    priceValues.sizeBigInt,
    marketData.assetDecimals
  );
  const orderTypeKey = formData.reduceOnly || isImplicitClose ? 'close' : 'open';

  const orderRequest: OrderRequest = {
    mt: MsgTypeOrderRequest,
    rq: requestId,
    mkt: marketData.id,
    acc: accountId,
    t: typeMapOnApi[formData.side][orderTypeKey],
    p: Number(finalPriceBigInt),
    s: Number(priceValues.sizeBigInt),
    fl: flags,
    lv: leverageForApi(formData.leverage),
    lb: NO_LAST_EXEC_BLOCK,
  };

  const userSlippage = isMarketOrder ? resolveUserSlippagePercent() : undefined;
  if (userSlippage != null) orderRequest.ms = Math.round(userSlippage * 100);

  if (isTriggerOrder) {
    orderRequest.tp = Number(priceValues.stopPriceBigInt);
    orderRequest.tpc = triggerCondition;
    if ('linkedPositionId' in formData && formData.linkedPositionId != null) {
      orderRequest.lp = formData.linkedPositionId;
    }
  }

  return orderRequest;
}

export function buildApiCancelOrder(order: Order, accountId: AccountID, requestId: RequestID): OrderRequest {
  const marketData = getMarketDataById(order.mkt);
  if (marketData?.id == null) {
    throw new Error('Market not found for the given marketId');
  }

  return {
    mt: MsgTypeOrderRequest,
    t: OrderTypeCancel,
    oid: order.oid,
    acc: accountId,
    rq: requestId,
    mkt: order.mkt,
    s: 0,
    fl: order.fl,
    lv: order.lv,
    tp: order.tp,
    tpc: order.tpc,
    lp: order.lp,
    lb: NO_LAST_EXEC_BLOCK,
  };
}

export function buildApiAddMarginOrder(
  marginAmount: bigint,
  marketId: number,
  accountId: AccountID,
  requestId: RequestID
): OrderRequest {
  const marketData = getMarketDataById(marketId);
  if (marketData?.id == null) {
    throw new Error('Market not found for the given marketId');
  }

  return {
    mt: MsgTypeOrderRequest,
    t: OrderTypeIncreasePositionCollateral,
    rq: requestId,
    mkt: marketData.id,
    acc: accountId,
    a: marginAmount.toString(),
    oid: 0,
    s: 0,
    fl: 0,
    lv: 1,
    lb: NO_LAST_EXEC_BLOCK,
  };
}

/** API change is executed by the engine as cancel-and-replace: the confirmed order gets a NEW oid. */
export function buildApiChangeOrder(
  order: Order,
  changes: OrderEditChanges,
  accountId: AccountID,
  requestId: RequestID
): OrderRequest {
  const marketData = getMarketDataById(order.mkt);
  if (marketData?.id == null) {
    throw new Error('Market not found for the given marketId');
  }

  const quoteDecimals = marketData.quoteDecimals;
  const assetDecimals = marketData.assetDecimals;

  const p = changes.price != null ? Number(bigToBigInt(stringToBig(String(changes.price)), quoteDecimals)) : order.p;
  const tp =
    changes.triggerPrice != null ? Number(bigToBigInt(stringToBig(String(changes.triggerPrice)), quoteDecimals)) : order.tp;
  const s =
    changes.size != null ? Number(bigToBigInt(stringToBig(String(changes.size)), assetDecimals)) : order.os - (order.fs || 0);

  return {
    mt: MsgTypeOrderRequest,
    t: OrderTypeChange,
    oid: order.oid,
    rq: requestId,
    mkt: order.mkt,
    acc: accountId,
    p,
    s,
    fl: order.fl,
    lv: order.lv,
    tp,
    tpc: order.tpc,
    lp: order.lp,
    lb: NO_LAST_EXEC_BLOCK,
  };
}

export function buildTriggerOrders(
  formData: OrderFormData,
  mainRequestId: RequestID,
  marketId: number,
  accountId: AccountID,
  getNextRequestId: () => RequestID
): OrderRequest[] {
  // Only Market and Limit orders support TP/SL in this flow
  if (formData.orderType !== OrderType.market && formData.orderType !== OrderType.limit) {
    return [];
  }

  const params = formData as OrderFormData & {
    tpSLEnabled?: boolean;
    takeProfit?: number;
    stopLoss?: number;
    tpType?: SLTPType;
    slType?: SLTPType;
    price?: string;
  };

  if (!params.tpSLEnabled) {
    return [];
  }

  const marketData = getMarketDataById(marketId);
  if (marketData?.id == null || !marketData.markPrice) {
    console.warn('Market data or mark price not available for trigger orders');
    return [];
  }

  const requests: OrderRequest[] = [];

  // Determine Reference Price
  let referencePrice = new Big(marketData.markPrice);
  if (formData.orderType === OrderType.limit && params.price) {
    referencePrice = stringToBig(params.price);
    if (referencePrice.eq(0)) {
      console.warn('Invalid limit price, falling back to mark price');
      referencePrice = new Big(marketData.markPrice);
    }
  }

  const priceValues = extractPriceValues(formData, marketData);
  const size = Number(priceValues.sizeBigInt);

  const convertToFixed = (val: Big) => Number(bigToBigInt(val, marketData.quoteDecimals));

  // IoC (market-like) vs GtC (limit-like), per feature flag.
  const useIoc = shouldTpSlOrdersUseIoc();
  const tpSlFlags: OrderFlags = useIoc ? OrderFlagImmediateOrCancel : OrderFlagGoodTillCancel;
  // IoC legs execute as market orders, so they carry the user's custom slippage like any market order.
  const userSlippage = useIoc ? resolveUserSlippagePercent() : undefined;

  // GtC legs fire as limit orders with limit price == trigger price; IoC legs are market orders
  // (p=0) that fill on trigger. `tp`/`tpc` carry the trigger condition either way.
  const createOrder = (triggerPrice: number, condition: number, requestId: RequestID): OrderRequest => {
    const triggerSide = formData.side === Side.long ? Side.short : Side.long;
    const orderTypeElement = typeMapOnApi[triggerSide].close;

    const orderRequest: OrderRequest = {
      mt: MsgTypeOrderRequest,
      rq: requestId,
      mkt: marketId,
      acc: accountId,
      t: orderTypeElement,
      p: useIoc ? 0 : triggerPrice,
      s: size,
      fl: tpSlFlags,
      lv: leverageForApi(formData.leverage),
      tp: triggerPrice,
      tpc: condition,
      tr: mainRequestId,
      lb: 0,
    };
    if (userSlippage != null) orderRequest.ms = Math.round(userSlippage * 100);

    return orderRequest;
  };

  // 1. Take Profit
  if (params.takeProfit) {
    const condition = getTpSlTriggerCondition(formData.side, true);
    const tpPrice = calculateTriggerPrice(
      params.tpType,
      params.takeProfit,
      referencePrice,
      formData.side,
      true,
      formData.leverage
    );
    requests.push(createOrder(convertToFixed(tpPrice), condition, getNextRequestId()));
  }

  // 2. Stop Loss
  if (params.stopLoss) {
    const condition = getTpSlTriggerCondition(formData.side, false);
    const slPrice = calculateTriggerPrice(
      params.slType,
      params.stopLoss,
      referencePrice,
      formData.side,
      false,
      formData.leverage
    );
    requests.push(createOrder(convertToFixed(slPrice), condition, getNextRequestId()));
  }

  return requests;
}
